/** Acquisition data is separate from the placement of an internal CTA. */
export function acquisitionFromReferrer(referrer: string): { source: string; medium: string } {
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '')
    if (host === 'itsbaba.com' || host.endsWith('.itsbaba.com')) return { source: '(direct)', medium: '(none)' }
    if (/^google\.[a-z.]+$/.test(host)) return { source: 'google', medium: 'organic' }
    if (host === 'bing.com' || host.endsWith('.bing.com')) return { source: 'bing', medium: 'organic' }
    if (host === 'search.yahoo.com') return { source: 'yahoo', medium: 'organic' }
    if (host === 'duckduckgo.com') return { source: 'duckduckgo', medium: 'organic' }
    return { source: host.slice(0, 100), medium: 'referral' }
  } catch {
    return { source: '(direct)', medium: '(none)' }
  }
}

/** Never retain a query, fragment, or a credential-bearing app route. */
export function acquisitionPath(value: string): string {
  try {
    const path = new URL(value, 'https://www.itsbaba.com').pathname
    if (/^\/(?:auth|account|reset|share|chat\/(?:shared|x\/share)|email\/claim)(?:\/|$)/i.test(decodeURIComponent(path))) return '/private'
    return path.slice(0, 200)
  } catch { return '/' }
}

export function referrerOrigin(value: string): string {
  try { return new URL(value).origin } catch { return '' }
}

/** Click ids that prove a paid (or at least ad-network) click. */
export const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid'] as const

/**
 * `click_key` is the random id that joins this web visit to the app install
 * (it travels in the OneLink as af_sub1 / deep_link_sub1). `fbp`, `fbc` and
 * `ttp` are the Meta and TikTok browser ids for server-side event matching.
 */
export const ACQUISITION_KEYS = [
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'utm_source', 'utm_medium',
  'utm_campaign', 'utm_term', 'utm_content', 'gad_source', 'gad_campaignid',
  'click_ts', 'landing_path', 'referrer', 'click_key', 'fbp', 'fbc', 'ttp',
] as const

/** Keys whose values are opaque ids: a stored value outside this alphabet is not ours. */
const OPAQUE_ID_KEYS = new Set<string>(['ttclid', 'msclkid', 'click_key', 'fbp', 'fbc', 'ttp'])
const OPAQUE_ID = /^[A-Za-z0-9._~-]{1,200}$/

/** Cookies and storage can outlive this version of the site. Validate on read. */
export function normalizeAcquisition(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const normalized: Record<string, string> = {}
  for (const key of ACQUISITION_KEYS) {
    if (typeof raw[key] !== 'string' || !raw[key]) continue
    if (OPAQUE_ID_KEYS.has(key) && !OPAQUE_ID.test(raw[key])) continue
    normalized[key] = key === 'landing_path' ? acquisitionPath(raw[key])
      : key === 'referrer' ? referrerOrigin(raw[key]) : raw[key].slice(0, 200)
  }
  return Object.keys(normalized).length ? normalized : null
}

/** A campaign must be replaced as a unit, including click IDs absent in the new one. */
export function hasAcquisition(value: Record<string, unknown> | null | undefined): boolean {
  return Boolean(value && [...CLICK_ID_KEYS, 'utm_source'].some(key => typeof value[key] === 'string' && value[key]))
}

export function hasClickId(value: Record<string, unknown> | null | undefined): boolean {
  return Boolean(value && CLICK_ID_KEYS.some(key => typeof value[key] === 'string' && value[key]))
}

const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

/**
 * Whether a freshly parsed visit replaces the stored acquisition. A new click id
 * always does. A bare UTM (the extension's welcome page says utm_source=extension)
 * never displaces a stored paid click: the ad paid for the visitor, the owned
 * channel only carried them. Without a stored click, a new UTM source replaces.
 */
export function shouldReplaceAcquisition(
  existing: Record<string, unknown> | null | undefined,
  incoming: Record<string, unknown> | null | undefined,
): boolean {
  if (!hasAcquisition(incoming)) return false
  if (!hasAcquisition(existing)) return true
  if (hasClickId(incoming)) return CLICK_ID_KEYS.some(key => incoming![key] && incoming![key] !== existing![key])
  if (hasClickId(existing)) return false
  return CAMPAIGN_KEYS.some(key => incoming![key] && incoming![key] !== existing![key])
}

/** Identifiers that need ad_storage before they may rest in a cookie or in storage. */
export const AD_IDENTIFIER_KEYS = [...CLICK_ID_KEYS, 'click_key', 'fbp', 'fbc', 'ttp'] as const

/**
 * What may be written to the shared cookie and to sessionStorage. Campaign names, the
 * landing path and the referrer origin are not identifiers and always persist; click ids,
 * the click key and the pixel ids persist only once ad_storage is granted. The full record
 * stays in memory for the page, so a visitor who accepts a moment later loses nothing.
 */
export function persistableAcquisition(
  record: Record<string, string> | null | undefined,
  adStorageGranted: boolean,
): Record<string, string> | null {
  if (!record) return null
  if (adStorageGranted) return record
  const out: Record<string, string> = { ...record }
  for (const key of AD_IDENTIFIER_KEYS) delete out[key]
  return Object.keys(out).length ? out : null
}

export function newClickKey(): string {
  try { return crypto.randomUUID() } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`
  }
}

function cookieValue(cookies: string, name: string): string {
  try {
    const row = cookies.split(';').map(part => part.trim()).find(part => part.startsWith(`${name}=`))
    return row ? decodeURIComponent(row.slice(name.length + 1)) : ''
  } catch { return '' }
}

/** Meta's click cookie format: fb.<subdomain index>.<creation time ms>.<fbclid>. */
export function fbcFromFbclid(fbclid: string, clickTs?: string, nowMs: number = Date.now()): string {
  const ms = Date.parse(clickTs || '') || nowMs
  return `fb.1.${ms}.${fbclid}`
}

/**
 * Meta and TikTok browser ids, read from the cookies their pixels set. Meta's
 * fbc is rebuilt from the fbclid when the pixel has not written the cookie yet
 * (it loads after consent, the click happens before). Only call this once the
 * visitor has allowed ad_storage; the cookies do not exist before that anyway.
 */
export function pixelIdentifiersFromCookies(
  cookies: string,
  acquisition?: Record<string, string> | null,
  nowMs: number = Date.now(),
): { fbp?: string; fbc?: string; ttp?: string } {
  const valid = (value: string) => (OPAQUE_ID.test(value) ? value : '')
  const fbp = valid(cookieValue(cookies, '_fbp'))
  let fbc = valid(cookieValue(cookies, '_fbc'))
  if (!fbc && acquisition?.fbclid) fbc = fbcFromFbclid(acquisition.fbclid, acquisition.click_ts, nowMs)
  const ttp = valid(cookieValue(cookies, '_ttp'))
  return { ...(fbp ? { fbp } : {}), ...(fbc ? { fbc } : {}), ...(ttp ? { ttp } : {}) }
}

/** What goes to the API next to an email, a checkout or a store click: the stored click plus live pixel ids. */
export function clickSignals(
  acquisition: Record<string, string> | null | undefined,
  cookies: string,
  options: { includePixelIds: boolean; nowMs?: number },
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const key of ACQUISITION_KEYS) if (acquisition?.[key]) out[key] = acquisition[key]
  delete out.fbp; delete out.fbc; delete out.ttp
  if (options.includePixelIds) Object.assign(out, pixelIdentifiersFromCookies(cookies, acquisition, options.nowMs))
  return out
}

/** Body of POST /click-key. The click key is the join to the app install. */
export function clickKeyBody(signals: Record<string, string>): Record<string, string> | null {
  if (!signals.click_key) return null
  const body: Record<string, string> = { clickKey: signals.click_key }
  for (const [from, to] of [
    ['gclid', 'gclid'], ['gbraid', 'gbraid'], ['wbraid', 'wbraid'], ['fbclid', 'fbclid'], ['ttclid', 'ttclid'],
    ['fbp', 'fbp'], ['fbc', 'fbc'], ['ttp', 'ttp'], ['landing_path', 'landingPath'], ['referrer', 'referrer'],
  ] as const) if (signals[from]) body[to] = signals[from]
  return body
}

/**
 * Carry the web click into a OneLink. AppsFlyer attributes the install; the
 * click key rides as af_sub1 and deep_link_sub1 so the app can post it to its
 * Worker, which joins the install to the stored click ids. Google ids pass
 * through under their own names for Google's attribution.
 */
export function oneLinkWithClickSignals(url: URL, signals: Record<string, string> | null | undefined): URL {
  if (!signals) return url
  if (signals.click_key) {
    url.searchParams.set('af_sub1', signals.click_key)
    url.searchParams.set('deep_link_sub1', signals.click_key)
  }
  for (const key of ['gclid', 'gbraid', 'wbraid'] as const) if (signals[key]) url.searchParams.set(key, signals[key])
  if (signals.gad_campaignid) url.searchParams.set('af_c_id', signals.gad_campaignid)
  for (const key of CAMPAIGN_KEYS) if (signals[key]) url.searchParams.set(key, signals[key])
  return url
}

export function normalizeGaClientId(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length > 64) return null
  return raw.trim().match(/^(?:GA\d+\.\d+\.)?(\d{5,12}\.\d{9,12})$/)?.[1] ?? null
}

export function normalizeGaSessionId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  return /^\d{6,20}$/.test(raw.trim()) ? raw.trim() : null
}

export function gaSessionFromCookie(raw: string): string | null {
  const value = raw.startsWith('GS2.')
    ? raw.slice(raw.indexOf('.', 4) + 1).match(/(?:^|\$)s(\d{6,20})(?:\$|$)/)?.[1]
    : raw.match(/^GS1\.\d\.(\d{6,20})(?:\.|$)/)?.[1]
  return value ?? null
}

export function gaIdentifiersFromCookies(cookies: string, measurementId = 'G-EW40258KBP'): { ga_client_id?: string; ga_session_id?: string } {
  function value(name: string): string {
    try {
      const row = cookies.split(';').map(row => row.trim()).find(row => row.startsWith(`${name}=`))
      return row ? decodeURIComponent(row.slice(name.length + 1)) : ''
    } catch { return '' }
  }
  const client = normalizeGaClientId(value('_ga'))
  const session = gaSessionFromCookie(value(`_ga_${measurementId.replace(/^G-/, '')}`))
  return { ...(client ? { ga_client_id: client } : {}), ...(session ? { ga_session_id: session } : {}) }
}

/**
 * The Stripe Checkout Session id of a create-checkout response: the API's own field when it
 * sends one, otherwise the cs_… id inside the hosted checkout URL.
 */
export function checkoutSessionId(response: { sessionId?: unknown; session_id?: unknown; eventId?: unknown; url?: unknown } | null | undefined): string | null {
  for (const candidate of [response?.sessionId, response?.session_id, response?.eventId]) {
    if (typeof candidate === 'string' && /^cs_(?:live|test)_[A-Za-z0-9]{10,200}$/.test(candidate)) return candidate
  }
  if (typeof response?.url === 'string') return response.url.match(/cs_(?:live|test)_[A-Za-z0-9]{10,200}/)?.[0] ?? null
  return null
}

/** Body of POST /checkout-signals, sent before the redirect to Stripe. Consent decides which ids go. */
export function checkoutSignalsBody(
  sessionId: string,
  signals: Record<string, string>,
  consent: Record<string, unknown> | undefined,
): Record<string, unknown> {
  return {
    sessionId,
    ...(signals.fbp ? { fbp: signals.fbp } : {}),
    ...(signals.fbc ? { fbc: signals.fbc } : {}),
    ...(signals.ttclid ? { ttclid: signals.ttclid } : {}),
    ...(signals.ttp ? { ttp: signals.ttp } : {}),
    ...(consent ? { consent } : {}),
  }
}

/**
 * The id Meta and TikTok dedupe the browser's CompleteRegistration against: the sign-up response's
 * `eventId` (`reg_<userId>`) when it came, otherwise the same string built from the platform user id.
 */
export function registrationEventId(serverEventId?: string | null, userId?: unknown): string | undefined {
  if (serverEventId && /^reg_[A-Za-z0-9_-]{1,100}$/.test(serverEventId)) return serverEventId
  return typeof userId === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(userId) ? `reg_${userId}` : undefined
}
